import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Seller } from './seller';
import { Farm, FarmAddress, FarmAddressForm, FarmForm } from './farm';
import {
  AddressWithCoordinates,
  AddressWithCoordinatesForm,
  Client,
  ClientForm,
  ClientWorkCondition,
  StorageKeeper,
} from './client';
import { Rate } from './rate';
import { CargoPlace, CargoPlaceJsonForm } from './cargo-place';
import { PackageType } from './package-type';
import { TransformToFormControl } from './common';
import { Payment } from './payment';
import { OrganizationMember } from './organization-member';

export type Cargo = {
  cargoPlacesQuantityJson: CargoPlaceQuantityItem[];
  creationDate: string;
  id: number;
  uniqueKey: string;
  orderGroups: CargoOrderGroup[];
  paymentsJson: CargoPayment[];
  sellerJson: SellerJson;
  trailerType: Nullable<string>;
  transportType: Nullable<string>;
  uploadDate: Nullable<string>;
  unloadDate: Nullable<string>;
  userId: Nullable<number>;
  loadingAddressesJson: CargoLoadingAddress[];
  unloadingAddressesJson: CargoUnloadingAddress[];
  carrierJson: CarrierJson;
  status: Nullable<string>;
  transitPointsJson: CargoTransitPoint[];
  grossWeight: Nullable<number>;
  payments: Payment[];
  temperatureRegime: Nullable<string>;
  divisionId: number;
};
export type SellerJson = {
  [key in keyof Seller]: Nullable<Seller[key]>;
};

export type SellerJsonForm = TransformToFormControl<SellerJson>;

export type CargoPayment = {
  sum: Nullable<number>;
  currencyLetter: Nullable<string>;
};

export type CargoPlaceQuantityItem = {
  quantity: Nullable<number>;
  type: Nullable<string>;
};

export type CargoOrderGroup = {
  id: number;
  uniqueKey: string;
  cargoId: number;
  clientJson: ClientJson;
  farmJson: FarmJson;
  flowerType: Nullable<string>;
  imported: Nullable<boolean>;
  orders: CargoOrder[];
};

export type FarmJson = {
  [key in keyof Farm]: Nullable<Farm[key]>;
} & {
  flowerTypesJson: string[];
  addressesJson: FarmAddress[];
  members: OrganizationMember[];
};

export type ClientJson = {
  [key in keyof Client]: Nullable<Client[key]>;
} & {
  addressesWithCoordinatesJson: AddressWithCoordinates[];
  storageKeepersJson: StorageKeeper[];
  members: OrganizationMember[];
  workCondition: ClientWorkCondition;
};

export type CargoPlaceJson = {
  [key in keyof Omit<CargoPlace, 'quantityInTrailerJson'>]: Nullable<
    CargoPlace[key]
  >;
} & {
  quantityInTrailerJson: CargoPlace['quantityInTrailerJson'];
};

export type RateJson = {
  [key in keyof Rate]: Nullable<Rate[key]>;
};

export type PackageTypeJson = {
  [key in keyof PackageType]: Nullable<PackageType[key]>;
};

export type CargoOrderForm = {
  [key in keyof Omit<
    CargoOrder,
    | 'rateJson'
    | 'farmJson'
    | 'clientJson'
    | 'clientAddressWithCoordinatesJson'
    | 'cargoPlacesJson'
    | 'farmAddress'
    | 'brokerJson'
    | 'farmBrokerJson'
  >]: FormControl<CargoOrder[key]>;
} & {
  rateJson: FormGroup<TransformToFormControl<CargoOrder['rateJson']>>;
  cargoPlacesJson: FormArray<FormGroup<OrderCargoPlaceDetailsForm>>;
  farmJson: FormGroup<FarmForm>;
  clientJson: FormGroup<ClientForm>;
  clientAddressWithCoordinatesJson: FormGroup<AddressWithCoordinatesForm>;
  farmAddress: FormGroup<FarmAddressForm>;
  brokerJson: FormGroup<SellerJsonForm>;
  farmBrokerJson: FormGroup<FarmForm>;
};

export type CargoOrderFormWithFactPackages = Omit<
  CargoOrderForm,
  'cargoPlacesJson'
> & {
  cargoPlacesJson: FormArray<
    FormGroup<OrderCargoPlaceDetailsFormWithFactPackageForm>
  >;
};

export type CargoOrder = {
  id: Nullable<number>;
  uniqueKey: Nullable<string>;
  orderGroupId: Nullable<number>;
  rateJson: RateJson;
  countryLetter: Nullable<string>;
  airWaybillNumber: Nullable<string>;
  precooling: Nullable<boolean>;
  cold: Nullable<boolean>;
  weight: Nullable<number>;
  buyerCode: Nullable<string>;
  farmJson: FarmJson;
  farmBrokerJson: FarmJson;
  farmAddress: FarmAddress;
  clientJson: ClientJson;
  flowerType: Nullable<string>;
  imported: Nullable<boolean>;
  cargoId: Nullable<number>;
  attachmentId: Nullable<number>;
  preOrderId: Nullable<number>;
  invoiceNumber: Nullable<string>;
  invoiceDate: Nullable<string>;
  invoiceCost: Nullable<number>;
  invoiceCurrencyLetter: Nullable<string>;
  clientAddressWithCoordinatesJson: AddressWithCoordinates;
  precoolingWeight: Nullable<number>;
  cargoPlacesJson: OrderCargoPlaceDetails[];
  divisionId: Nullable<number>;
  brokerJson: SellerJson;
  prepayment: Nullable<boolean>;
  prepaymentCurrencyLetter: Nullable<string>;
  prepaymentSum: Nullable<number>;
  paymentDate: Nullable<string>;
  expectedLoadingDate: Nullable<string>;
};

export type OrderCargoPlaceDetails = {
  box: Nullable<boolean>;
  packageTypeJson: PackageTypeJson;
  packageQuantityLimit: Nullable<number>;
  packageQuantity: Nullable<number>;
  cargoPlaceJson: CargoPlaceJson;
  cargoPlaceQuantity: Nullable<number>;
  factCargoPlaceQuantity: Nullable<number>;
  factPackageTypesJson: FactPackageType[];
};

export type OrderCargoPlaceDetailsForm = TransformToFormControl<
  Omit<OrderCargoPlaceDetails, 'cargoPlaceJson' | 'packageTypeJson'>
> & {
  packageTypeJson: FormGroup<
    TransformToFormControl<OrderCargoPlaceDetails['packageTypeJson']>
  >;
  cargoPlaceJson: FormGroup<CargoPlaceJsonForm>;
};

export type OrderCargoPlaceDetailsFormWithFactPackageForm =
  TransformToFormControl<
    Omit<
      OrderCargoPlaceDetails,
      'cargoPlaceJson' | 'packageTypeJson' | 'factPackageTypesJson'
    >
  > & {
    packageTypeJson: FormGroup<
      TransformToFormControl<OrderCargoPlaceDetails['packageTypeJson']>
    >;
    cargoPlaceJson: FormGroup<CargoPlaceJsonForm>;
    factPackageTypesJson: FormArray<FormGroup<FactPackageTypeForm>>;
  };

export type FactPackageType = {
  packageTypeJson: PackageTypeJson;
  packageQuantityLimit: Nullable<number>;
  packageQuantity: Nullable<number>;
};

export type PackageTypeJsonForm = FormGroup<
  TransformToFormControl<PackageTypeJson>
>;

export type FactPackageTypeForm = TransformToFormControl<
  Omit<FactPackageType, 'packageTypeJson'>
> & {
  packageTypeJson: PackageTypeJsonForm;
};

export enum CargoStatus {
  LOADING = 'LOADING',
  ON_THE_WAY = 'ON_THE_WAY',
  SVH = 'SVH',
  DELIVERY = 'DELIVERY',
}

export type CargoLoadingAddress = {
  farmName: Nullable<string>;
  address: Nullable<string>;
  latitude: Nullable<string>;
  longitude: Nullable<string>;
  attachmentId: Nullable<number>;
  comment: Nullable<string>;
};

export type CargoLoadingAddressForm =
  TransformToFormControl<CargoLoadingAddress>;

export type CargoUnloadingAddress = {
  clientName: Nullable<string>;
  address: Nullable<string>;
  latitude: Nullable<string>;
  longitude: Nullable<string>;
  approximateLocation: Nullable<boolean>;
};

export type CargoUnloadingAddressForm =
  TransformToFormControl<CargoUnloadingAddress>;

export type CarrierJson = {
  id: Nullable<number>;
  uniqueKey: Nullable<string>;
  name: Nullable<string>;
  cost: Nullable<number>;
  currencyLetter: Nullable<string>;
  paymentType: Nullable<string>;
  transports: CarrierJsonTransport[];
  additionalPayments: CarrierAdditionalPayment[];
};

export type CarrierAdditionalPayment = {
  comment: Nullable<string>;
  sum: Nullable<number>;
  currencyLetter: Nullable<string>;
};
export type CarrierAdditionalPaymentForm =
  TransformToFormControl<CarrierAdditionalPayment>;

export type CarrierJsonForm = TransformToFormControl<
  Omit<CarrierJson, 'additionalPayments' | 'transports'>
> & {
  transports: FormArray<FormGroup<CarrierJsonTransportFormWithGuid>>;
  additionalPayments: FormArray<FormGroup<CarrierAdditionalPaymentForm>>;
};

export type CarrierJsonTransport = {
  transportNumber: Nullable<string>;
  trailerNumber: Nullable<string>;
  driverSurname: Nullable<string>;
  driverName: Nullable<string>;
  driverPhone: Nullable<string>;
  comment: Nullable<string>;
  loadingDate: Nullable<string>;
  transportWeight: Nullable<number>;
  trailerWeight: Nullable<number>;
  attachmentId: Nullable<number>;
};

export type CarrierJsonTransportForm =
  TransformToFormControl<CarrierJsonTransport>;

export type CarrierJsonTransportFormWithGuid = CarrierJsonTransportForm & {
  guid: FormControl<Nullable<string>>;
};

export type CreateEmptyCargoPayload = {
  cargo: Omit<Cargo, 'id' | 'creationDate' | 'uniqueKey'> & {
    id: null;
    creationDate: null;
    uniqueKey: null;
  };
  preOrderIds: number[];
};

export type CargoTransitPoint = {
  transitPoint: {
    id: Nullable<number>;
    type: Nullable<string>;
    name: Nullable<string>;
    address: Nullable<string>;
    latitude: Nullable<string>;
    longitude: Nullable<string>;
  };
  displayOnRoute: Nullable<boolean>;
};

export type CargoTransitPointForm = {
  [key in keyof Omit<CargoTransitPoint, 'transitPoint'>]: FormControl<
    CargoTransitPoint[key]
  >;
} & {
  guid: FormControl<string>;
  transitPoint: FormGroup<
    TransformToFormControl<CargoTransitPoint['transitPoint']>
  >;
};

export interface CargoArchiveJournalResponse {
  items: CargoInJournal[];
  totalCount: number;
}

export type CargoInJournal = {
  id: number;
  creationDate: Nullable<string>;
  userId: number;
  status: CargoStatus;
  lastName: Nullable<string>;
  firstName: Nullable<string>;
  paymentsExists: boolean;
  transportNumber: Nullable<string>;
  updated: Nullable<string>;
  comment: Nullable<string>; // location comment
  remark: Nullable<string>;
  address: Nullable<string>;
  temperatureRegime: Nullable<string>;
  dataSentTo1C: Nullable<boolean>;
  divisionId: Nullable<number>;
  farmCountries: Nullable<string>;
  transportType: Nullable<string>;
  trailerType: Nullable<string>;
  carrierJson: CarrierJson;
  uploadDate: Nullable<string>;
  svhArrivalDate: Nullable<string>;
  flowerTypes: Nullable<string>;
  trash: Nullable<boolean>;
  invoiceDetailsList: Nullable<CargoInvoiceDetails[]>;
  svhDepartureDate: Nullable<string>;
  archive: Nullable<boolean>;
  sellerJson: SellerJson;
  clientNames: string[];
  specificationIds: number[];
};

export type CargoInvoiceDetails = {
  number: Nullable<number>;
  date: Nullable<string>;
};

export type CargoArchiveJournalFilter = {
  trash: boolean;
  carrierName: string;
  clientName: string;
  transportNumber: string;
  invoiceNumber: string;
  weekNumber: Nullable<number>;
};

export type CargoArchiveJournalFilterForm =
  TransformToFormControl<CargoArchiveJournalFilter>;
